/*
 * Copyright (C) 2025 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and
 * should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly
 * prohibited.
 */

/* eslint-disable import/no-named-default, no-nested-ternary */
import { values } from 'ramda';
import { combineEpics, ofType } from 'redux-observable';
import { combineReducers } from 'redux';
import { connectRouter } from 'connected-react-router';
import { map, switchMap, takeUntil } from 'rxjs/operators';
import { timer } from 'rxjs';
import * as TerminalsEpics from './terminals/epics';
import * as AntennasEpics from './antennas/epics';
import * as BeamsEpics from './beams/epics';
import * as HubsEpics from './hubs/epics';
import * as GroundStationsEpics from './groundStations/epics';
import * as MapsEpics from './maps/epics';
import * as TerminalHistoryEpic from './terminalHistory/epics';
import * as MissionPlansEpic from './missionPlans/epics';
import * as FlightLegsEpic from './flightLegs/epics';
import * as FlightLegServiceAlertsEpic from './flightLegServiceAlerts/epics';
import * as ConfigEpic from './config/epics';
import * as SteerableBeamsEpic from './steerableBeams/epics';
import * as TicketsEpic from './tickets/epics';
import * as DataUsageEpic from './dataUsage/epics';
import * as ServiceTierRequestsEpic from './serviceTierRequests/epics';
import * as ManualDowntimePeriods from './manualDowntimePeriods/epics';
import * as OrganizationsEpic from './organizations/epics';
import * as ContractsEpic from './contracts/epics';
import * as RolesEpic from './roles/epics';
import * as ClientsEpic from './clients/epics';
import * as OptionsEpic from './options/epics';
import * as GpsSaEpic from './gpsSa/epics';
import * as WhatsNewEpic from './whatsNew/epics';
import * as HaloNetGroundStationsEpic from './haloNetGroundStations/epics';
import * as HaloNetSpaceVehiclesEpic from './haloNetSpaceVehicles/epics';
import { default as session, State as SessionState } from './session/reducer';
import { default as system, State as SystemState } from './system/reducer';
import { ajaxRaw } from '../utils/StoreUtils';
import { default as terminals, State as TerminalsState } from './terminals/reducer';
import { default as antennas, State as AntennasState } from './antennas/reducer';
import { default as beams, State as BeamsState } from './beams/reducer';
import { default as hubs, State as HubsState } from './hubs/reducer';
import { default as groundStations, GroundStationsState } from './groundStations/reducer';
import { default as dataUsage, State as DataUsageState } from './dataUsage/reducer';
import tickets, { TicketsState } from './tickets/reducer';
import { default as ui, SortDirection, State as UiState } from './ui/reducer';
import { default as terminalHistory, State as TerminalHistoryState } from './terminalHistory/reducer';
import { default as missionPlans, State as MissionPlansState } from './missionPlans/reducer';
import { default as maps, OverlaysContextsEnum, State as MapsState } from './maps/reducer';
import { default as flightLegs, State as FlightLegsState } from './flightLegs/reducer';
import {
  default as flightLegServiceAlerts,
  State as FlightLegServiceAlertsState,
} from './flightLegServiceAlerts/reducer';
import { default as config, State as ConfigState } from './config/reducer';
import { default as steerableBeams, State as SteerableBeamsState } from './steerableBeams/reducer';
import { default as serviceTierRequests, State as ServiceTierRequestsState } from './serviceTierRequests/reducer';
import { default as manualDowntimePeriods, State as ManualDowntimePeriodsState } from './manualDowntimePeriods/reducer';
import { default as organizations, State as OrganizationsState } from './organizations/reducer';
import { default as contracts, State as ContractsState } from './contracts/reducer';
import { default as roles, State as RolesState } from './roles/reducer';
import { default as clients, State as ClientsState } from './clients/reducer';
import { default as options, State as OptionsState } from './options/reducer';
import { default as gpsSa, State as GpsSaState } from './gpsSa/reducer';
import { default as whatsNew, State as WhatsNewState } from './whatsNew/reducer';
import { default as haloNetGroundStations, HaloNetGroundStationsState } from './haloNetGroundStations/reducer';
import { default as haloNetSpaceVehicles, HaloNetSpaceVehiclesState } from './haloNetSpaceVehicles/reducer';
import Terminal, { SpaceNetworkInfo, SteerableBeamPriority, TerminalDetails } from '../shared/entities/terminal';
import { Authorities } from '../shared/entities/user';
import Config, { LoginConfig } from '../shared/entities/config';
import {
  buildTerminalsInBeamList,
  getHaloNetSpaceNetworks,
  getSelectedTerminalTypeLabelBasedOnTab,
  getTerminalDisplayName,
  terminalTypeSortPriorityFromTerminalTypeLabel,
} from '../utils/TerminalUtilities';
import { isActive, isOnline } from '../utils/ConnectionStatusUtils';
import { ResourceFilter } from '../components/common/table/types';
import { TerminalPlatformsEnum } from '../shared/entities/terminalPlatform';
import { OrbitPoint, OrbitPointsBySvName } from '../shared/entities/haloNet';
import {
  DEFAULT_HALONET_POSITION_FALLBACK_AFTER_SECONDS,
  isPositionWithheld,
  overlaySpaceVehiclePosition,
  toSpaceVehicleChartHistory,
} from '../utils/SpaceVehiclePositionUtils';
import TerminalHistory from '../shared/entities/terminalHistory';
import toLower from 'lodash/toLower';
import { durationToNowInMin, MINUTES_IN_HOUR, SEC_TO_MS_CONVERSION } from '../utils/DateTimeUtils';
import FlightLeg from '../shared/entities/flightLeg';
import { TypedUseSelectorHook, useSelector } from 'react-redux';
import { GOOGLE_MAP_INSTANCE_KEY } from '../components/common/map/GoogleMapSingleton';
import { ServiceTierRequestStatus } from '../shared/entities/serviceTierRequest';
import { PlatformListGroup, SpaceVehicleGroups } from '../shared/entities/platformListGroup';
import { PortalsEnum } from '../shared/entities/portal';
import { isNil } from 'lodash';

export interface EpicDependencies {
  ajaxRaw: typeof ajaxRaw;
}

export interface RootState {
  router: any;
  session: SessionState;
  system: SystemState;
  terminals: TerminalsState;
  antennas: AntennasState;
  beams: BeamsState;
  hubs: HubsState;
  groundStations: GroundStationsState;
  tickets: Readonly<TicketsState>;
  ui: UiState;
  terminalHistory: TerminalHistoryState;
  missionPlans: MissionPlansState;
  maps: MapsState;
  flightLegs: FlightLegsState;
  flightLegServiceAlerts: FlightLegServiceAlertsState;
  config: ConfigState;
  steerableBeams: SteerableBeamsState;
  dataUsage: DataUsageState;
  serviceTierRequests: ServiceTierRequestsState;
  manualDowntimePeriods: ManualDowntimePeriodsState;
  organizations: OrganizationsState;
  contracts: ContractsState;
  roles: RolesState;
  clients: ClientsState;
  options: OptionsState;
  gpsSa: GpsSaState;
  whatsNew: WhatsNewState;
  haloNetGroundStations: HaloNetGroundStationsState;
  haloNetSpaceVehicles: HaloNetSpaceVehiclesState;
}

export const pollingTimer = (action$: any, pollingIntervalSeconds: number, actionTypeToCancel: string) =>
  switchMap($action =>
    timer(0, pollingIntervalSeconds * SEC_TO_MS_CONVERSION).pipe(
      map(i => $action), // Send the action through to the next observables instead of the timer interval
      takeUntil(action$.pipe(ofType(actionTypeToCancel))),
    ),
  );

export const rootEpic = combineEpics(
  ...values(TerminalsEpics),
  ...values(AntennasEpics),
  ...values(BeamsEpics),
  ...values(HubsEpics),
  ...values(GroundStationsEpics),
  ...values(MapsEpics),
  ...values(TerminalHistoryEpic),
  ...values(MissionPlansEpic),
  ...values(FlightLegsEpic),
  ...values(FlightLegServiceAlertsEpic),
  ...values(ConfigEpic),
  ...values(SteerableBeamsEpic),
  ...values(TicketsEpic),
  ...values(DataUsageEpic),
  ...values(ServiceTierRequestsEpic),
  ...values(ManualDowntimePeriods),
  ...values(OrganizationsEpic),
  ...values(ContractsEpic),
  ...values(RolesEpic),
  ...values(ClientsEpic),
  ...values(OptionsEpic),
  ...values(GpsSaEpic),
  ...values(WhatsNewEpic),
  ...values(HaloNetGroundStationsEpic),
  ...values(HaloNetSpaceVehiclesEpic),
);

// reducer name maps to state tree its responsible for
const rootReducer = (history: History) =>
  combineReducers<RootState>({
    router: connectRouter(history),
    session,
    system,
    terminals,
    antennas,
    beams,
    hubs,
    groundStations,
    tickets,
    ui,
    terminalHistory,
    missionPlans,
    maps,
    flightLegs,
    flightLegServiceAlerts,
    config,
    steerableBeams,
    dataUsage,
    serviceTierRequests,
    manualDowntimePeriods,
    organizations,
    contracts,
    roles,
    clients,
    options,
    gpsSa,
    whatsNew,
    haloNetGroundStations,
    haloNetSpaceVehicles,
  });

export default rootReducer;

// UI selectors
export const selectPlatformListDrawerOpen = (state: RootState) =>
  state.ui.overlaysContextUiState[state.ui.overlaysCurrentContext].platformListDrawerOpen;
export const selectDetailsPanelOpen = (state: RootState) =>
  state.ui.overlaysContextUiState[state.ui.overlaysCurrentContext].detailsPanelOpen;
export const selectTerminalsInYourBeamPanelShown = (state: RootState) => state.ui.terminalsInYourBeamPanelShown;
export const selectGroundStationVisibleStations = (state: RootState): Record<string, boolean> => {
  return state.haloNetGroundStations.visibleStations;
};
export const selectHiddenTerminalsList = (state: RootState) =>
  state.ui.overlaysContextUiState[state.ui.overlaysCurrentContext].hiddenTerminalsList;
export const selectHiddenTerminalsInYourBeamDatapathUuids = (state: RootState) =>
  state.ui.hiddenTerminalsInYourBeamDatapathUuids;
export const selectSelectedDatapathUuidInPlatformList = (state: RootState) =>
  state.ui.overlaysContextUiState[state.ui.overlaysCurrentContext].selectedDatapathUuidInPlatformList;
export const selectSelectedDatapathUuidInFleetView = (state: RootState) =>
  state.ui.overlaysContextUiState[OverlaysContextsEnum.FLEET_VIEW].selectedDatapathUuidInPlatformList;
export const selectSelectedDatapathUuidInTransportMonitoring = (state: RootState) =>
  state.ui.overlaysContextUiState[OverlaysContextsEnum.TRANSPORT_MONITORING].selectedDatapathUuidInPlatformList;
export const selectSelectedTerminalTabIndexInTransportMonitoring = (state: RootState) =>
  state.ui.overlaysContextUiState[OverlaysContextsEnum.TRANSPORT_MONITORING].selectedTerminalTabIndex;
export const selectSelectedTerminalTabIndex = (state: RootState) =>
  state.ui.overlaysContextUiState[state.ui.overlaysCurrentContext].selectedTerminalTabIndex;
export const selectTerminalType = (state: RootState) =>
  getSelectedTerminalTypeLabelBasedOnTab(
    state.ui.overlaysContextUiState[state.ui.overlaysCurrentContext].selectedTerminalTabIndex,
    state.terminals.tailDetailsPanelTerminal,
  );
export const selectPlatformListSortDirection = (state: RootState) =>
  state.ui.overlaysContextUiState[state.ui.overlaysCurrentContext].platformListSortDirection;
export const selectPlatformListSortField = (state: RootState) =>
  state.ui.overlaysContextUiState[state.ui.overlaysCurrentContext].platformListSortField;
export const selectVisibleTerminalsInYourBeamListSortDirection = (state: RootState) =>
  state.ui.visibleTerminalsInYourBeamListSortDirection;
export const selectVisibleTerminalsInYourBeamListSortField = (state: RootState) =>
  state.ui.visibleTerminalsInYourBeamListSortField;
export const selectTerminalFilters = (state: RootState): ResourceFilter[] =>
  state.ui.overlaysContextUiState[state.ui.overlaysCurrentContext].terminalFilters;
export const selectCollapsedTerminalsInPlatformList = (state: RootState) =>
  state.ui.overlaysContextUiState[state.ui.overlaysCurrentContext].collapsedTerminalsInPlatformList;
export const selectTerminalVisibilityToggleState = (state: RootState) =>
  state.ui.overlaysContextUiState[state.ui.overlaysCurrentContext].terminalVisibilityToggleState;
export const selectHoveredAnnotation = (state: RootState) => state.ui.hoveredAnnotation;
export const selectTooltipStepMap = (state: RootState) => state.ui.tooltipStepMap;
export const selectFtvTerminalsInYourBeamCollapsedTerminals = (state: RootState) =>
  state.ui.ftvTerminalsInYourBeamCollapsedTerminals;
export const selectIsInitialTerminalsRequestFinished = (state: RootState) =>
  state.terminals.isInitialTerminalsRequestFinished;
export const selectAllProtocolsModalVisible = (state: RootState) =>
  !!state.terminalHistory.allProtocolsModalTimestamp && !!state.terminalHistory.allProtocolsRateType;

// mission plan UI selectors
export const selectIsRequestingFlightLegServiceAlerts = (state: RootState) =>
  state.flightLegServiceAlerts.isRequestingFlightLegServiceAlerts;
export const selectOverlayMissionMissionListVisible = (state: RootState) => state.ui.overlayMissionMissionListVisible;
export const selectOverlayMissionFlightLegListVisible = (state: RootState) =>
  state.ui.overlayMissionFlightLegListVisible;
export const selectOverlayMissionModalVisible = (state: RootState) => state.ui.overlayMissionModalVisible;

// Returns the terminals as a map
export const selectTerminals = (state: RootState) =>
  Object.keys(state.terminals.terminals)
    .filter(
      (datapathUuid: string) =>
        state.terminals.terminals[datapathUuid].platformType !== TerminalPlatformsEnum.SpaceVehicle,
    )
    .reduce(
      (terminals: { [datapathUuid: string]: Terminal }, datapathUuid: string) => ({
        ...terminals,
        [datapathUuid]: state.terminals.terminals[datapathUuid],
      }),
      {},
    );
// Returns the terminals as a map with terminalUuid as key
// needed only until terminalId in ServiceTierChangeRequest is changed to datapathUuid
export const selectTerminalsWithTerminalUuidKey = (state: RootState) =>
  Object.keys(state.terminals.terminalsByTerminalUuid)
    .filter(
      (terminalUuid: string) =>
        state.terminals.terminalsByTerminalUuid[terminalUuid].platformType !== TerminalPlatformsEnum.SpaceVehicle,
    )
    .reduce(
      (terminals: { [terminalUuid: string]: Terminal }, terminalUuid: string) => ({
        ...terminals,
        [terminalUuid]: state.terminals.terminalsByTerminalUuid[terminalUuid],
      }),
      {},
    );

// How old a position reported by a space vehicle may be before the propagated one is used.
// The config slice is empty until /config returns, so fall back to the built in value.
const selectHaloNetPositionFallbackAfterSeconds = (state: RootState): number =>
  state.config?.appConfig?.haloNetPositionFallbackAfterSeconds ?? DEFAULT_HALONET_POSITION_FALLBACK_AFTER_SECONDS;

// The names of the space vehicles that HaloNet reports.
const selectHaloNetSpaceVehicleNames = (state: RootState): Set<string> =>
  new Set(state.haloNetSpaceVehicles.spaceVehicles.map(spaceVehicle => spaceVehicle.svName));

// True when the terminal is a space vehicle that HaloNet also reports.
// The test fails closed: a terminal with no haloNetSpaceVehicleName never shows.
const isHaloNetLinkedSpaceVehicle = (terminal: Terminal, haloNetSvNames: Set<string>): boolean =>
  terminal.platformType === TerminalPlatformsEnum.SpaceVehicle &&
  !!terminal.haloNetSpaceVehicleName &&
  haloNetSvNames.has(terminal.haloNetSpaceVehicleName);

/**
 * The space vehicle terminals that HaloNet also reports, each with the HaloNet propagated
 * position put onto it.
 *
 * Every space vehicle selector reads this one function, so the filter and the position
 * cannot get out of step with each other.
 */
const selectHaloNetLinkedSpaceVehicleTerminalsAsArray = (state: RootState): Terminal[] => {
  const haloNetSvNames = selectHaloNetSpaceVehicleNames(state);
  const positionsBySvName = state.haloNetSpaceVehicles.currentPositionsBySvName;
  const fallbackAfterSeconds = selectHaloNetPositionFallbackAfterSeconds(state);

  return Object.values(state.terminals.terminals)
    .filter((terminal: Terminal) => isHaloNetLinkedSpaceVehicle(terminal, haloNetSvNames))
    .map((terminal: Terminal) =>
      overlaySpaceVehiclePosition(
        terminal,
        positionsBySvName[terminal.haloNetSpaceVehicleName as string],
        fallbackAfterSeconds,
      ),
    );
};

export const selectSpaceVehicleTerminals = (state: RootState) =>
  selectHaloNetLinkedSpaceVehicleTerminalsAsArray(state).reduce(
    (spaceVehicleTerminals: { [datapathUuid: string]: Terminal }, terminal: Terminal) => ({
      ...spaceVehicleTerminals,
      [terminal.datapathUuid]: terminal,
    }),
    {},
  );
export const selectLoadingTerminals = (state: RootState) => state.terminals.isRequestingTerminals;
export const selectRequestTailDetailsPanelTerminalError = (state: RootState) =>
  state.terminals.requestTailDetailsPanelTerminalError;
export const selectSelectedTerminalFlightPath =
  (portal = PortalsEnum.UNIFIED_PORTAL) =>
  (state: RootState) =>
    state.terminals.selectedTerminalFlightPath[portal];

// Returns the terminals mapped to an array
export const selectTerminalsAsArray = (state: RootState) =>
  Object.keys(state.terminals.terminals).map((datapathUuid: string) => state.terminals.terminals[datapathUuid]);
const selectSpaceVehiclesAsArray = (state: RootState) => selectHaloNetLinkedSpaceVehicleTerminalsAsArray(state);

// User/auth selectors
export const selectRolesForCurrentUser = (state: RootState): Authorities[] | undefined =>
  (state.session && state.session.tokens && state.session.tokens.accessToken && state.session.tokens.accessToken.claims
    ? state.session.tokens.accessToken.claims
    : {})['authorities'] as Authorities[];
export const selectUserData = (state: RootState) => state.session.tokens?.idToken;
export const selectIsAuthenticated = (state: RootState) => state.session.authenticated;
export const selectShouldSignOut = (state: RootState) => state.session.shouldSignOut;
export const selectWhatsNewLatestChecksum = (state: RootState): string | undefined => state.whatsNew.latestChecksum;
export const selectWhatsNewUserLastViewedChecksum = (state: RootState): string | undefined =>
  state.whatsNew.userLastViewedChecksum;
export const selectWhatsNewMarkdownContent = (state: RootState): string => state.whatsNew.markdownContent;
export const selectWhatsNewPanelOpen = (state: RootState): boolean => state.whatsNew.isPanelOpen;

export interface TerminalListItem {
  datapathUuid: string;
  aircraftSerialNumber?: string;
  terminalDisplayName?: string;
  connectivityStatus: string;
  collapsed?: boolean;
  hidden?: boolean;
  selected?: boolean;
  datapathUuidsInGroup: string[];
  platformType: TerminalPlatformsEnum;
  showRing2: boolean;
  showRing3: boolean;
  restrictionEnabled: boolean;
  flSignalQuality: number | null | undefined;
  spaceNetworks?: SpaceNetworkInfo[];
}

export const connectionStatusPriorityFunc =
  (terminals: { [key: string]: Terminal }) => (finalStatus: string, datapathUuidInGroup: string) => {
    const termConnStatus = (terminals[datapathUuidInGroup] || {}).connectivityStatus;

    // get the highest priority status for the terminal

    // active state
    if (isActive(finalStatus)) {
      return finalStatus;
    } else if (isActive(termConnStatus)) {
      return termConnStatus;

      // standby state
    } else if (isOnline(finalStatus)) {
      return finalStatus;
    } else if (isOnline(termConnStatus)) {
      return termConnStatus;
    }

    // offline
    return termConnStatus;
  };

const sortTerminalListItems = (sortField: string) => (a: any, b: any) => {
  const aValue = a[sortField];
  const bValue = b[sortField];

  if (!aValue) {
    return 1;
  } else if (!bValue) {
    return -1;
  }

  const alphebeticSort = toLower(a.terminalDisplayName) > toLower(b.terminalDisplayName) ? 1 : -1;

  if (sortField === 'connectivityStatus') {
    if (aValue === bValue) {
      return alphebeticSort;
    } else if (isActive(aValue)) {
      return -1;
    } else if (isActive(bValue)) {
      return 1;
    } else if (isOnline(aValue)) {
      return -1;
    } else if (isOnline(bValue)) {
      return 1;
    }
  }

  return alphebeticSort;
};

export const selectPlatformListItemsUnfiltered = (state: RootState): TerminalListItem[] => {
  const seenKeyGroups: { [keyGroup: string]: boolean } = {};

  const items: TerminalListItem[] = Object.values(state.terminals.terminals)
    .filter(
      (terminal: Terminal) =>
        state.terminals.terminals[terminal.datapathUuid].platformType !== TerminalPlatformsEnum.SpaceVehicle,
    )
    .reduce((acc: TerminalListItem[], curr: Terminal) => {
      const terminalsIdsInGroupSortedByTerminalTypeLabel = getTerminalsIdsInGroupSortedByTerminalTypeLabel(
        curr,
        state.terminals.terminals,
      ).join(',');
      if (seenKeyGroups[terminalsIdsInGroupSortedByTerminalTypeLabel]) {
        return acc;
      }
      seenKeyGroups[terminalsIdsInGroupSortedByTerminalTypeLabel] = true;
      const datapathUuidsInGroup = [curr.datapathUuid, ...(curr.datapathUuidsOnAircraft || [])].filter(
        (datapathUuid: string) => datapathUuid,
      );
      return [
        ...acc,
        {
          flSignalQuality: curr.flSignalQuality,
          networkGroup: curr.networkGroup,
          datapathUuid: curr.datapathUuid,
          aircraftSerialNumber: curr.aircraftSerialNumber,
          terminalDisplayName: [
            getTerminalDisplayName(curr),
            ...datapathUuidsInGroup.map((datapathUuidInGroup: string) =>
              getTerminalDisplayName(state.terminals.terminals[datapathUuidInGroup] || {}),
            ),
          ].find((displayText: string | undefined) => displayText),
          connectivityStatus: datapathUuidsInGroup.reduce(
            connectionStatusPriorityFunc(state.terminals.terminals),
            'offline',
          ),
          collapsed: !!datapathUuidsInGroup.find(
            (datapathUuidInGroup: string) =>
              state.ui.overlaysContextUiState[state.ui.overlaysCurrentContext].collapsedTerminalsInPlatformList[
                datapathUuidInGroup
              ],
          ),
          hidden: !!datapathUuidsInGroup.find((datapathUuidInGroup: string) =>
            state.ui.overlaysContextUiState[state.ui.overlaysCurrentContext].hiddenTerminalsList.includes(
              datapathUuidInGroup,
            ),
          ),
          selected: !!datapathUuidsInGroup.find(
            (datapathUuidInGroup: string) =>
              state.ui.overlaysContextUiState[state.ui.overlaysCurrentContext].selectedDatapathUuidInPlatformList ===
              datapathUuidInGroup,
          ),
          datapathUuidsInGroup: getTerminalsIdsInGroupSortedByTerminalTypeLabel(curr, state.terminals.terminals),
          platformType: curr.platformType,
          restrictionEnabled: curr.restrictionEnabled,
          showRing2:
            curr.steerableBeamPriority === SteerableBeamPriority.PRIMARY ||
            curr.steerableBeamPriority === SteerableBeamPriority.SECONDARY ||
            !!(curr.datapathUuidsOnAircraft || []).find(
              (datapathUuid: string) =>
                ((state.terminals.terminals[datapathUuid] as Terminal) || {}).steerableBeamPriority ===
                  SteerableBeamPriority.PRIMARY ||
                ((state.terminals.terminals[datapathUuid] as Terminal) || {}).steerableBeamPriority ===
                  SteerableBeamPriority.SECONDARY,
            ),
          showRing3:
            curr.steerableBeamPriority === SteerableBeamPriority.PRIMARY ||
            !!(curr.datapathUuidsOnAircraft || []).find(
              (datapathUuid: string) =>
                ((state.terminals.terminals[datapathUuid] as Terminal) || {}).steerableBeamPriority ===
                SteerableBeamPriority.PRIMARY,
            ),
        } as TerminalListItem,
      ];
    }, [] as TerminalListItem[])
    .sort(sortTerminalListItems(state.ui.overlaysContextUiState[state.ui.overlaysCurrentContext].platformListSortField))
    .map((e: any, i: number, a: any[]) =>
      state.ui.overlaysContextUiState[state.ui.overlaysCurrentContext].platformListSortDirection[
        PlatformListGroup.TERMINAL
      ] === SortDirection.ASCENDING
        ? e
        : a[a.length - 1 - i],
    );

  return items;
};

export const selectPlatformListItems = (state: RootState): TerminalListItem[] => {
  return selectPlatformListItemsUnfiltered(state).filter(terminalListItem =>
    state.ui.overlaysContextUiState[state.ui.overlaysCurrentContext].terminalFilters.every(
      ({ key, value }) =>
        (state.terminals.terminals[terminalListItem.datapathUuid] || {})[key] === value ||
        !!terminalListItem.datapathUuidsInGroup.find(
          (datapathUuidInGroup: string) => (state.terminals.terminals[datapathUuidInGroup] || {})[key] === value,
        ),
    ),
  );
};

export const selectSpaceVehicleListItemsUnfilteredAndUnsorted = (state: RootState): TerminalListItem[] => {
  const seenKeyGroups: { [keyGroup: string]: boolean } = {};

  const items: TerminalListItem[] = selectHaloNetLinkedSpaceVehicleTerminalsAsArray(state).reduce(
    (acc: TerminalListItem[], curr: Terminal) => {
      const terminalsIdsInGroupSortedByTerminalTypeLabel = getTerminalsIdsInGroupSortedByTerminalTypeLabel(
        curr,
        state.terminals.terminals,
      ).join(',');
      if (seenKeyGroups[terminalsIdsInGroupSortedByTerminalTypeLabel]) {
        return acc;
      }
      seenKeyGroups[terminalsIdsInGroupSortedByTerminalTypeLabel] = true;
      const datapathUuidsInGroup = [curr.datapathUuid, ...(curr.datapathUuidsOnAircraft || [])].filter(
        (datapathUuid: string) => datapathUuid,
      );
      return [
        ...acc,
        {
          flSignalQuality: curr.flSignalQuality,
          networkGroup: curr.networkGroup,
          datapathUuid: curr.datapathUuid,
          aircraftSerialNumber: curr.aircraftSerialNumber,
          terminalDisplayName: [
            getTerminalDisplayName(curr),
            ...datapathUuidsInGroup.map((datapathUuidInGroup: string) =>
              getTerminalDisplayName(state.terminals.terminals[datapathUuidInGroup] || {}),
            ),
          ].find((displayText: string | undefined) => displayText),
          connectivityStatus: datapathUuidsInGroup.reduce(
            connectionStatusPriorityFunc(state.terminals.terminals),
            'offline',
          ),
          collapsed: !!datapathUuidsInGroup.find(
            (datapathUuidInGroup: string) =>
              state.ui.overlaysContextUiState[state.ui.overlaysCurrentContext].collapsedTerminalsInPlatformList[
                datapathUuidInGroup
              ],
          ),
          hidden: !!datapathUuidsInGroup.find((datapathUuidInGroup: string) =>
            state.ui.overlaysContextUiState[state.ui.overlaysCurrentContext].hiddenTerminalsList.includes(
              datapathUuidInGroup,
            ),
          ),
          selected: !!datapathUuidsInGroup.find(
            (datapathUuidInGroup: string) =>
              state.ui.overlaysContextUiState[state.ui.overlaysCurrentContext].selectedDatapathUuidInPlatformList ===
              datapathUuidInGroup,
          ),
          datapathUuidsInGroup: getTerminalsIdsInGroupSortedByTerminalTypeLabel(curr, state.terminals.terminals),
          platformType: curr.platformType,
          restrictionEnabled: curr.restrictionEnabled,
          showRing2: false, // Space Vehicles will never be on HCX
          showRing3: false, // Space Vehicles will never be on HCX
          spaceNetworks: getHaloNetSpaceNetworks(
            curr,
            state.terminals.terminals[(curr.datapathUuidsOnAircraft || [])[0]],
          ),
        } as TerminalListItem,
      ];
    },
    [] as TerminalListItem[],
  );
  return items;
};

const selectConnectedSpaceVehicleListItems = (state: RootState): TerminalListItem[] => {
  const spaceVehicleListItems = selectSpaceVehicleListItemsUnfilteredAndUnsorted(state);
  return spaceVehicleListItems
    .filter((spaceVehicleListItem: TerminalListItem) => spaceVehicleListItem.connectivityStatus !== 'offline')
    .sort(sortTerminalListItems(state.ui.overlaysContextUiState[state.ui.overlaysCurrentContext].platformListSortField))
    .map((e: any, i: number, a: any[]) =>
      state.ui.overlaysContextUiState[state.ui.overlaysCurrentContext].platformListSortDirection[
        PlatformListGroup.CONNECTED
      ] === SortDirection.ASCENDING
        ? e
        : a[a.length - 1 - i],
    );
};

const selectDisconnectedSpaceVehicleListItems = (state: RootState): TerminalListItem[] => {
  const spaceVehicleListItems = selectSpaceVehicleListItemsUnfilteredAndUnsorted(state);
  return spaceVehicleListItems
    .filter((spaceVehicleListItem: TerminalListItem) => spaceVehicleListItem.connectivityStatus === 'offline')
    .sort(sortTerminalListItems(state.ui.overlaysContextUiState[state.ui.overlaysCurrentContext].platformListSortField))
    .map((e: any, i: number, a: any[]) =>
      state.ui.overlaysContextUiState[state.ui.overlaysCurrentContext].platformListSortDirection[
        PlatformListGroup.DISCONNECTED
      ] === SortDirection.ASCENDING
        ? e
        : a[a.length - 1 - i],
    );
};

export const selectGroupedSpaceVehicleListItems = (state: RootState): SpaceVehicleGroups<TerminalListItem[]> => {
  const connectedSpaceVehicleListItems = selectConnectedSpaceVehicleListItems(state);
  const disconnectedSpaceVehicleListItems = selectDisconnectedSpaceVehicleListItems(state);

  return {
    [PlatformListGroup.CONNECTED]: connectedSpaceVehicleListItems,
    [PlatformListGroup.DISCONNECTED]: disconnectedSpaceVehicleListItems,
  };
};

export const selectConnectedSpaceVehicles = (state: RootState): Terminal[] => {
  const spaceVehicles = selectSpaceVehiclesAsArray(state);
  return spaceVehicles.filter((spaceVehicle: Terminal) => spaceVehicle.connectivityStatus !== 'offline');
};

export const selectDisconnectedSpaceVehicles = (state: RootState): Terminal[] => {
  const spaceVehicles = selectSpaceVehiclesAsArray(state);
  return spaceVehicles.filter((spaceVehicle: Terminal) => spaceVehicle.connectivityStatus === 'offline');
};

export const selectGroupedSpaceVehicles = (state: RootState): SpaceVehicleGroups<Terminal[]> => {
  const connectedSpaceVehicles = selectConnectedSpaceVehicles(state);
  const disconnectedSpaceVehicles = selectDisconnectedSpaceVehicles(state);

  return {
    [PlatformListGroup.CONNECTED]: connectedSpaceVehicles,
    [PlatformListGroup.DISCONNECTED]: disconnectedSpaceVehicles,
  };
};

export const selectTerminalsInYourBeamListItems = (state: RootState): TerminalListItem[] => {
  // If the tail details panel terminal has not yet loaded, return an empty array
  if (!state.terminals.tailDetailsPanelTerminal) {
    return [];
  }

  const seenKeyGroups: { [keyGroup: string]: boolean } = {};
  const terminalsInBeam = selectTerminalsInBeam(state);
  const items: TerminalListItem[] = terminalsInBeam
    .reduce((acc: TerminalListItem[], curr: Terminal) => {
      const terminalsIdsInGroupSortedByTerminalTypeLabel = getTerminalsIdsInGroupSortedByTerminalTypeLabel(
        curr,
        state.terminals.terminals,
      ).join(',');
      if (seenKeyGroups[terminalsIdsInGroupSortedByTerminalTypeLabel]) {
        return acc;
      }
      seenKeyGroups[terminalsIdsInGroupSortedByTerminalTypeLabel] = true;
      const datapathUuidsInGroup = [curr.datapathUuid, ...(curr.datapathUuidsOnAircraft || [])].filter(
        (datapathUuid: string) => datapathUuid,
      );

      return [
        ...acc,
        {
          terminalDisplayName: [
            getTerminalDisplayName(curr),
            ...datapathUuidsInGroup.map((datapathUuidInGroup: string) =>
              getTerminalDisplayName(state.terminals.terminals[datapathUuidInGroup] || {}),
            ),
          ].find((displayText: string | undefined) => displayText),
          connectivityStatus: datapathUuidsInGroup.reduce(
            connectionStatusPriorityFunc(state.terminals.terminals),
            'offline',
          ),
          collapsed: !!datapathUuidsInGroup.find(
            (datapathUuidInGroup: string) => state.ui.ftvTerminalsInYourBeamCollapsedTerminals[datapathUuidInGroup],
          ),
          hidden: !!datapathUuidsInGroup.find((datapathUuidInGroup: string) =>
            state.ui.hiddenTerminalsInYourBeamDatapathUuids.includes(datapathUuidInGroup),
          ),
          selected: !!datapathUuidsInGroup.find(
            (datapathUuidInGroup: string) =>
              state.ui.overlaysContextUiState[state.ui.overlaysCurrentContext].selectedDatapathUuidInPlatformList ===
              datapathUuidInGroup,
          ),
          restrictionEnabled: curr.restrictionEnabled,
          datapathUuidsInGroup: getTerminalsIdsInGroupSortedByTerminalTypeLabel(curr, state.terminals.terminals),
          platformType: curr.platformType,
          showRing2:
            curr.steerableBeamPriority === SteerableBeamPriority.PRIMARY ||
            curr.steerableBeamPriority === SteerableBeamPriority.SECONDARY ||
            !!(curr.datapathUuidsOnAircraft || []).find(
              (datapathUuid: string) =>
                ((state.terminals.terminals[datapathUuid] as Terminal) || {}).steerableBeamPriority ===
                  SteerableBeamPriority.PRIMARY ||
                ((state.terminals.terminals[datapathUuid] as Terminal) || {}).steerableBeamPriority ===
                  SteerableBeamPriority.SECONDARY,
            ),
          showRing3:
            curr.steerableBeamPriority === SteerableBeamPriority.PRIMARY ||
            !!(curr.datapathUuidsOnAircraft || []).find(
              (datapathUuid: string) =>
                ((state.terminals.terminals[datapathUuid] as Terminal) || {}).steerableBeamPriority ===
                SteerableBeamPriority.PRIMARY,
            ),
        } as TerminalListItem,
      ];
    }, [] as TerminalListItem[])
    .sort(sortTerminalListItems(state.ui.visibleTerminalsInYourBeamListSortField))
    .map((e: any, i: number, a: any[]) =>
      state.ui.visibleTerminalsInYourBeamListSortDirection === SortDirection.ASCENDING ? e : a[a.length - 1 - i],
    );

  return items;
};

const getTerminalsIdsInGroupSortedByTerminalTypeLabel = (
  terminal: Terminal,
  terminals: { [datapathUuid: string]: Terminal },
): string[] => {
  return [terminal.datapathUuid, ...(terminal.datapathUuidsOnAircraft || [])]
    .map((datapathUuid: string) => terminals[datapathUuid])
    .filter((terminal: Terminal) => terminal)
    .sort((a: Terminal, b: Terminal) =>
      terminalTypeSortPriorityFromTerminalTypeLabel(a.terminalTypeLabel) >
      terminalTypeSortPriorityFromTerminalTypeLabel(b.terminalTypeLabel)
        ? 1
        : -1,
    )
    .map((terminal: Terminal) => terminal.datapathUuid);
};

export const selectIsRequestingTailDetailsPanelTerminal = (state: RootState) =>
  state.terminals.isRequestingTailDetailsPanelTerminal;

export const selectTerminalsInBeam = (state: RootState) =>
  buildTerminalsInBeamList(
    state.terminals.tailDetailsPanelTerminal,
    state.ui.overlaysContextUiState[state.ui.overlaysCurrentContext].selectedTerminalTabIndex,
  ).filter((terminalInBeam: Terminal) => durationToNowInMin(terminalInBeam.sourceTime) <= MINUTES_IN_HOUR) || [];
export const selectTerminalsInBeamLength = (state: RootState) => selectTerminalsInBeam(state).length;
export const selectExportViewTitle = (state: RootState) => state.ui.exportViewTitle;
export const selectAntennas = (state: RootState) => state.antennas.antennas;
export const selectGroundStationCoverage = (state: RootState) => state.groundStations.coverage;
export const selectHaloNetGroundStations = (state: RootState) => state.haloNetGroundStations.groundStations;
export const selectHaloNetGroundStationAvailability = (state: RootState) => state.haloNetGroundStations.availability;
export const selectHaloNetGroundStationReservations = (state: RootState) => state.haloNetGroundStations.reservations;
export const selectIsRequestingHaloNetGroundStations = (state: RootState) =>
  state.haloNetGroundStations.isRequestingGroundStations;
export const selectIsRequestingHaloNetGroundStationAvailability = (state: RootState) =>
  state.haloNetGroundStations.isRequestingAvailability;
export const selectIsRequestingHaloNetGroundStationReservations = (state: RootState) =>
  state.haloNetGroundStations.isRequestingReservations;
export const selectHaloNetGroundStationsError = (state: RootState) => state.haloNetGroundStations.groundStationsError;
export const selectHaloNetGroundStationAvailabilityError = (state: RootState) =>
  state.haloNetGroundStations.availabilityError;
export const selectHaloNetGroundStationReservationsError = (state: RootState) =>
  state.haloNetGroundStations.reservationsError;
export const selectLoadingAntennas = (state: RootState) => state.antennas.isRequestingAntennas;
export const selectBeams = (state: RootState) => state.beams.beams;
export const selectFlRlContoursAvailable = (state: RootState) => state.beams.flRlContoursAvailable;
export const selectHeatMaps = (state: RootState) => state.beams.heatMaps;
export const selectIsRequestingFlHeatMaps = (state: RootState) => state.beams.isRequestingFlHeatMaps;
export const selectIsRequestingRlHeatMaps = (state: RootState) => state.beams.isRequestingRlHeatMaps;
export const selectFleetViewAntenna = (state: RootState) => state.ui.fleetViewAntenna;
export const selectSteerableBeams = (state: RootState) => state.steerableBeams;
export const selectSteerableBeamsRingList = (state: RootState) => state.steerableBeams.beams;
export const selectDistanceToBeamEdgeNmMinorThreshold = (state: RootState) =>
  state.steerableBeams.distanceToBeamEdgeNmMinorThreshold;
export const selectDistanceToBeamEdgeNmMajorThreshold = (state: RootState) =>
  state.steerableBeams.distanceToBeamEdgeNmMajorThreshold;

// mission planning tool selectors
export const selectMissionPlans = (state: RootState) => Object.values(state.missionPlans.missionPlans);
export const selectMissionPlansObj = (state: RootState) => state.missionPlans.missionPlans;
export const selectLoadingMissionPlans = (state: RootState) => state.missionPlans.isRequestingMissionPlans;
export const selectMissionPlansError = (state: RootState) => state.missionPlans.requestingMissionPlansError;
export const selectFlightLegs = (state: RootState) => state.flightLegs.flightLegs;
export const selectFlightLegsOrderedByLegOrder = (state: RootState) =>
  Object.keys(state.flightLegs.flightLegs)
    .map((flightLegId: string) => state.flightLegs.flightLegs[flightLegId])
    .sort((a: FlightLeg, b: FlightLeg) => (a.legOrder > b.legOrder ? 1 : -1));
export const selectPlansListSortDirection = (state: RootState) => state.missionPlans.plansListSortDirection;
export const selectPlansListSortField = (state: RootState) => state.missionPlans.plansListSortField;
export const selectSelectedAntenna = (state: RootState) => state.missionPlans.selectedAntenna;
export const selectSelectedTerminal = (state: RootState) => state.missionPlans.selectedTerminal;
export const selectSelectedIdOfMissionPlanById = (state: RootState) => state.missionPlans.missionPlanById.id;
export const selectMissionPlanById = (state: RootState) => state.missionPlans.missionPlanById;
export const selectLoadingMissionPlanById = (state: RootState) => state.missionPlans.requestingMissionPlanById;
export const selectMissionPlanByIdError = (state: RootState) => state.missionPlans.requestingMissionPlanByIdError;
export const selectSavingMissionPlanError = (state: RootState) => state.missionPlans.savingMissionPlanError;
export const selectMissionPlanSavedOrUpdated = (state: RootState) => state.missionPlans.missionPlanSavedOrUpdated;
export const selectOverrideRecommendedAntenna = (state: RootState) => state.missionPlans.overrideRecommendedAntenna;
export const selectMissionDetailsLoading = (state: RootState) => state.missionPlans.missionDetailsLoading;
export const selectDeleteConfirmation = (state: RootState) => state.missionPlans.deleteConfirmation;
export const selectDeletingPlanSuccess = (state: RootState) => state.missionPlans.deletingMissionPlanSuccess;
export const selectDeletingPlanError = (state: RootState) => state.missionPlans.deletingMissionPlanError;
export const selectSavingMissionPlan = (state: RootState) => state.missionPlans.savingMissionPlan;

export const selectFlightLegServiceAlerts = (state: RootState) => state.flightLegServiceAlerts.flightLegServiceAlerts;
export const selectRequestingFlightLegsError = (state: RootState) => state.flightLegs.requestingFlightLegsError;
export const selectIsRequestingFlightLegs = (state: RootState) => state.flightLegs.isRequestingFlightLegs;
export const selectFlightLegsInfo = (state: RootState) =>
  Object.keys(state.flightLegs.flightLegsInfo)
    .map((flightLegId: string) => state.flightLegs.flightLegsInfo[flightLegId])
    .sort((a: FlightLeg, b: FlightLeg) => (a.legOrder > b.legOrder ? 1 : -1));
export const selectFlightLegsInfoById = (state: RootState) => state.flightLegs.flightLegsInfo;
export const selectLoadingFlightLegsInfoSuccess = (state: RootState) =>
  state.flightLegs.requestingFlightLegsInfoSuccess;
export const selectSuccessfullyParsedFlightLegs = (state: RootState) =>
  state.flightLegs.uploadedFlightLegs.filter(({ error }) => !error);
export const selectFailedToParseFlightLegs = (state: RootState) => state.flightLegs.failedToParseFlightLegs;
export const selectDeleteLegConfirmationVisible = (state: RootState) => state.flightLegs.deleteLegConfirmation;
export const selectFlightLegToDelete = (state: RootState) => state.flightLegs.flightLegToDelete;
export const selectDeleteFlightLegSuccess = (state: RootState) => state.flightLegs.deleteFlightLegSuccess;
export const selectDeleteFlightLegError = (state: RootState) => state.flightLegs.deleteFlightLegError;
export const selectIsFlightInfoLoading = (state: RootState) =>
  state.flightLegs.isRequestingFlightLegsInfo || state.flightLegs.uploadingFlightLeg;
export const selectFlightInfoError = (state: RootState) =>
  state.flightLegs.requestingFlightLegsError ||
  state.flightLegs.uploadFlightLegError ||
  state.flightLegs.updateFlightLegsOrderError ||
  state.flightLegs.deleteFlightLegError;
export const selectIsPerformanceOutlookLoading = (state: RootState) =>
  state.config.isRequestingCompactExportEnabled ||
  state.missionPlans.requestingMissionPlanById ||
  state.flightLegs.isRequestingFlightLegsInfo ||
  state.flightLegServiceAlerts.isRequestingFlightLegServiceAlerts ||
  state.beams.isRequestingBeams;
export const selectPerformanceOutlookError = (state: RootState) =>
  state.config.requestingCompactExportEnabledError ||
  state.missionPlans.requestingMissionPlanByIdError ||
  state.flightLegs.requestingFlightLegsInfoError ||
  state.flightLegServiceAlerts.requestingFlightLegServiceAlertsError ||
  state.beams.requestBeamsError;
export const selectSelectedMissionPlanId = (state: RootState) => state.missionPlans.selectedMissionPlanId;
export const selectFlightLegVictoryChartData = (state: RootState) => state.missionPlans.flightLegVictoryChartData;

// session selectors
export const selectUserId = (state: RootState) => state.session.tokens?.idToken?.claims.email;

// ui selectors
export const selectRequestAntennasError = (state: RootState) => state.antennas.requestAntennasError;
export const selectRequestTerminalsError = (state: RootState) => state.terminals.requestTerminalsError;
export const selectOverlaidFlightLegHidden =
  (mapId = GOOGLE_MAP_INSTANCE_KEY) =>
  (state: RootState) =>
    state.maps.mapContext[mapId]?.overlaidFlightLegHidden;
export const selectSelectedOverlaidFlightLegId =
  (mapId = GOOGLE_MAP_INSTANCE_KEY) =>
  (state: RootState) =>
    state.flightLegs.mapContext[mapId]?.uiState[state.flightLegs.currentContext]?.selectedOverlaidFlightLegId;
export const selectselectedOverlaidMissionPlanId =
  (mapId = GOOGLE_MAP_INSTANCE_KEY) =>
  (state: RootState) =>
    state.flightLegs.mapContext[mapId]?.uiState[state.flightLegs.currentContext]?.selectedOverlaidMissionPlanId;
// The details panel terminal comes from /terminals/{datapathUuid} and is not in the terminals map,
// so it needs the HaloNet position put onto it here as well.
export const selectTerminalDetails = (state: RootState) => {
  const terminal = state.terminals.tailDetailsPanelTerminal;
  if (!terminal || terminal.platformType !== TerminalPlatformsEnum.SpaceVehicle || !terminal.haloNetSpaceVehicleName) {
    return terminal;
  }

  const position = state.haloNetSpaceVehicles.currentPositionsBySvName[terminal.haloNetSpaceVehicleName];

  return overlaySpaceVehiclePosition(
    terminal,
    position,
    selectHaloNetPositionFallbackAfterSeconds(state),
  ) as TerminalDetails;
};
export const selectTerminalDetailsTerminalUuid = (state: RootState) =>
  state.terminals.tailDetailsPanelTerminal ? state.terminals.tailDetailsPanelTerminal.terminalUuid : undefined;
export const selectTerminalDetailsDatapathUuid = (state: RootState) =>
  state.terminals.tailDetailsPanelTerminal ? state.terminals.tailDetailsPanelTerminal.datapathUuid : undefined;

// Aggregates all the aircraft data relevant to the UP Ruler tool with support for multiple terminals on the same aircraft
export const selectTerminalDetailsRulerData = (state: RootState) => {
  const primaryTerminal = state.terminals.tailDetailsPanelTerminal;

  let mostRecentTerminalSourceTime = primaryTerminal?.sourceTime;
  let currentLatitudeDeg = primaryTerminal?.latitudeDeg;
  let currentLongitudeDeg = primaryTerminal?.longitudeDeg;
  let currentVelocityKnots = primaryTerminal?.velocityKnots;
  let currentAltitudeFeet = primaryTerminal?.altitudeFeet;

  // Iterate through other terminals on aircraft and see if any have a more recent lat/long
  if (
    !isNil(mostRecentTerminalSourceTime) &&
    primaryTerminal?.terminalsOnAircraft &&
    primaryTerminal.terminalsOnAircraft.length > 0
  ) {
    primaryTerminal.terminalsOnAircraft.forEach((otherTerminal: TerminalDetails) => {
      if (otherTerminal && otherTerminal.sourceTime > (mostRecentTerminalSourceTime as number)) {
        mostRecentTerminalSourceTime = otherTerminal.sourceTime;
        currentLatitudeDeg = otherTerminal.latitudeDeg;
        currentLongitudeDeg = otherTerminal.longitudeDeg;
        currentVelocityKnots = otherTerminal.velocityKnots;
        currentAltitudeFeet = otherTerminal.altitudeFeet;
      }
    });
  }
  return {
    lat: currentLatitudeDeg,
    lng: currentLongitudeDeg,
    velocityKnots: currentVelocityKnots,
    altitudeFeet: currentAltitudeFeet,
  };
};

export const selectFlightPathBeamsChecked =
  (mapId = GOOGLE_MAP_INSTANCE_KEY) =>
  (state: RootState) =>
    state.maps.mapContext[mapId]?.overlaysUiState[state.maps.overlaysCurrentContext]?.flightPathBeamsChecked;
export const selectFlightPathWaypointsChecked =
  (mapId = GOOGLE_MAP_INSTANCE_KEY) =>
  (state: RootState) =>
    state.maps.mapContext[mapId]?.overlaysUiState[state.maps.overlaysCurrentContext]?.flightPathWaypointsChecked;

export const selectZoomLatLng =
  (mapId = GOOGLE_MAP_INSTANCE_KEY) =>
  (state: RootState) =>
    state.maps.mapContext[mapId]?.zoomLatLng;
export const selectSavedZoomSettings =
  (mapId = GOOGLE_MAP_INSTANCE_KEY) =>
  (state: RootState) =>
    state.maps.mapContext[mapId]?.savedZoomSettings;
export const selectMapHidden =
  (mapId = GOOGLE_MAP_INSTANCE_KEY) =>
  (state: RootState) =>
    state.maps.mapContext[mapId]?.hidden;

export const mapContext =
  (mapId = GOOGLE_MAP_INSTANCE_KEY) =>
  (state: RootState) =>
    state.maps.mapContext[mapId];
export const flightLegMapContext =
  (mapId = GOOGLE_MAP_INSTANCE_KEY) =>
  (state: RootState) =>
    state.flightLegs.mapContext[mapId];

// tail tag selectors
export const selectTailTagsShown =
  (mapId = GOOGLE_MAP_INSTANCE_KEY) =>
  (state: RootState) =>
    state.maps.mapContext[mapId]?.overlaysUiState[state.maps.overlaysCurrentContext]?.tailTagsShown;
export const selectTerminalTagSize =
  (mapId = GOOGLE_MAP_INSTANCE_KEY) =>
  (state: RootState) =>
    state.maps.mapContext[mapId]?.overlaysUiState[state.maps.overlaysCurrentContext]?.terminalTagSize;

// config selectors
export const selectIsFullTailViewEnabled = (state: RootState) => state.config.appConfig.isFullTailViewEnabled;
export const selectAppConfig = (state: RootState): Config => state.config.appConfig;
export const selectGoogleMapsAPIKey = (state: RootState): string => state.config.appConfig.googleMapsAPIKey;
export const selectGoogleMapsURL = (state: RootState): string => state.config.appConfig.googleMapsURL;
export const selectIsInitialConfigRequestFinished = (state: RootState): boolean =>
  state.config.isInitialConfigRequestFinished;
export const selectIsCompactExportEnabled = (state: RootState) => state.config.compactExportEnabled;
export const selectLoginConfig = (state: RootState): LoginConfig => state.config.appConfig.loginConfig;
export const selectIsLoginConfigLoaded = (state: RootState): boolean => state.config.isLoginConfigLoaded;
export const selectIsTthoEnabled = (state: RootState): boolean => state.config.appConfig.isTthoEnabled;

// router selectors
export const selectRouterPathname = (state: RootState) => state.router.location.pathname;
export const selectPrintMode = (state: RootState) => state.ui.printMode;

export const selectWeatherOverlayConfig = (state: RootState) => state.config.appConfig.weatherOverlayConfig;
export const selectHelpEmail = (state: RootState) => state.config.appConfig.loginConfig.helpEmail;
export const selectFeedbackEmail = (state: RootState) => state.config.appConfig.feedbackEmail;
export const selectHnarequestEmail = (state: RootState) => state.config.appConfig.hnarequestEmail;
export const selectServiceTierChangeRequestEmail = (state: RootState) =>
  state.config.appConfig.serviceTierChangeRequestEmail;
export const selectCustomerSupportEmail = (state: RootState) => state.config.appConfig.customerSupportEmail;

// terminal history selectors
export const selectTerminalHistory = (state: RootState) => state.terminalHistory.terminalHistory;
export const selectOtherTerminalHistory = (state: RootState) => state.terminalHistory.otherTerminalHistory;

export const selectIsRequestingTerminalHistory = (state: RootState) =>
  state.terminalHistory.isRequestingTerminalHistory;
export const selectRequestTerminalHistoryError = (state: RootState) =>
  state.terminalHistory.requestTerminalHistoryError;
export const selectTerminalHistoryAnnotations = (state: RootState) => state.terminalHistory.annotations;
export const selectChartsXAxisMin = (state: RootState) => state.terminalHistory.chartsXAxisMin;
export const selectChartsXAxisMax = (state: RootState) => state.terminalHistory.chartsXAxisMax;
export const selectChartsXAxisMaxZoom = (state: RootState) => state.terminalHistory.chartsXAxisMaxZoom;
export const selectChartsXAxisMinZoom = (state: RootState) => state.terminalHistory.chartsXAxisMinZoom;
export const selectChartsLastUpdated = (state: RootState) => state.terminalHistory.chartsLastUpdated;
export const selectAllProtocolsModalTimestamp = (state: RootState) => state.terminalHistory.allProtocolsModalTimestamp;
export const selectAllProtocolsModalRateType = (state: RootState) => state.terminalHistory.allProtocolsRateType;
export const selectAllProtocolsModalUseOtherTerminalHistory = (state: RootState) =>
  state.terminalHistory.allProtocolsUseOtherTerminalHistory;

// tickets selectors
export const selectTickets = (state: RootState) => state.tickets;
export const selectTicketsTotal = (state: RootState) => state.tickets.total;
export const selectTicketsOptions = (state: RootState) => state.tickets.options;
export const selectSalesforceBaseURL = (state: RootState) => state.config.appConfig.salesforceBaseURL;

export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

// FTV Mission Overlay Table
export const selectMissionOverlayMissionsSortField = (state: RootState) => state.ui.missionOverlayMissionsSortField;
export const selectMissionOverlayMissionsSortDirection = (state: RootState) =>
  state.ui.missionOverlayMissionsSortDirection;
export const selectMissionOverlayFlightLegsSortField = (state: RootState) => state.ui.missionOverlayFlightLegsSortField;
export const selectMissionOverlayFlightLegsSortDirection = (state: RootState) =>
  state.ui.missionOverlayFlightLegsSortDirection;

export const selectIsRequestingFlightPath = (state: RootState) => state.terminals.isRequestingFlightPath;
export const selectTailDetailsPanelTerminal = (state: RootState) => state.terminals.tailDetailsPanelTerminal;

// MPT Flight Leg Takeoff Time
export const selectIsUpdatingTakeoffTime = (state: RootState) => state.flightLegs.isUpdatingTakeoffTime;
export const selectUpdateTakeoffTimeError = (state: RootState) => state.flightLegs.updateTakeoffTimeError;

// Maps Selectors
export const selectOverlaysCurrentContext = (state: RootState) => state.maps.overlaysCurrentContext;
export const selectCurrentMapsOverlayState =
  (mapId = GOOGLE_MAP_INSTANCE_KEY) =>
  (state: RootState) =>
    state.maps.mapContext[mapId].overlaysUiState[state.maps.overlaysCurrentContext];
export const selectMapDimensions =
  (mapId = GOOGLE_MAP_INSTANCE_KEY) =>
  (state: RootState) =>
    state.maps.mapContext[mapId]?.mapDimensions;
export const selectMapPosition =
  (mapId = GOOGLE_MAP_INSTANCE_KEY) =>
  (state: RootState) =>
    state.maps.mapContext[mapId]?.mapPosition;
export const selectMissionPlanTakeoffMarker =
  (mapId = GOOGLE_MAP_INSTANCE_KEY) =>
  (state: RootState) =>
    state.maps.mapContext[mapId]?.missionPlanTakeoffMarker;
export const selectMissionPlanLandingMarker =
  (mapId = GOOGLE_MAP_INSTANCE_KEY) =>
  (state: RootState) =>
    state.maps.mapContext[mapId]?.missionPlanLandingMarker;
export const selectMissionPlanPathMarkers =
  (mapId = GOOGLE_MAP_INSTANCE_KEY) =>
  (state: RootState) =>
    state.maps.mapContext[mapId]?.missionPlanPathMarkers;
export const selectMapSettingsSatViewTileState =
  (mapId = GOOGLE_MAP_INSTANCE_KEY) =>
  (state: RootState) =>
    state.maps.mapContext[mapId]?.mapSettingsSatViewTileState;
export const selectMapSettingsLabelsTileState =
  (mapId = GOOGLE_MAP_INSTANCE_KEY) =>
  (state: RootState) =>
    state.maps.mapContext[mapId]?.mapSettingsLabelsTileState;
export const selectMapSettingsPrecipRadarTileState =
  (mapId = GOOGLE_MAP_INSTANCE_KEY) =>
  (state: RootState) =>
    state.maps.mapContext[mapId]?.mapSettingsPrecipRadarTileState;
export const selectMapSettingsFtvMapHeight =
  (mapId = GOOGLE_MAP_INSTANCE_KEY) =>
  (state: RootState) =>
    state.maps.mapContext[mapId]?.mapSettingsFtvMapHeight;
export const selectMapSettingsFtvMapSize =
  (mapId = GOOGLE_MAP_INSTANCE_KEY) =>
  (state: RootState) =>
    state.maps.mapContext[mapId]?.mapSettingsFtvMapSize;
export const selectPerformanceOutlookLegendPanelShown =
  (mapId = GOOGLE_MAP_INSTANCE_KEY) =>
  (state: RootState) =>
    state.maps.mapContext[mapId]?.performanceOutlookLegendPanelShown;
export const selectOverlaysPanelShown =
  (mapId = GOOGLE_MAP_INSTANCE_KEY) =>
  (state: RootState) =>
    state.maps.mapContext[mapId]?.overlaysPanelShown;
export const selectMapSettingsPanelShown =
  (mapId = GOOGLE_MAP_INSTANCE_KEY) =>
  (state: RootState) =>
    state.maps.mapContext[mapId]?.mapSettingsPanelShown;
export const selectTailTagOverlaysPanelShown =
  (mapId = GOOGLE_MAP_INSTANCE_KEY) =>
  (state: RootState) =>
    state.maps.mapContext[mapId]?.tailTagOverlaysPanelShown;
export const selectMapRulerToolShown =
  (mapId = GOOGLE_MAP_INSTANCE_KEY) =>
  (state: RootState) =>
    state.maps.mapContext[mapId]?.mapRulerToolShown;
export const selectMapRulerToolFollowAircraft =
  (mapId = GOOGLE_MAP_INSTANCE_KEY) =>
  (state: RootState) =>
    state.maps.mapContext[mapId]?.mapRulerToolFollowAircraft;
export const selectMapRulerToolWaypoints =
  (mapId = GOOGLE_MAP_INSTANCE_KEY) =>
  (state: RootState) =>
    state.maps.mapContext[mapId]?.mapRulerToolWaypoints;
export const selectMapRulerToolLegData =
  (mapId = GOOGLE_MAP_INSTANCE_KEY) =>
  (state: RootState) =>
    state.maps.mapContext[mapId]?.mapRulerToolLegData;
export const selectMapRulerToolMarkersAndPolylines =
  (mapId = GOOGLE_MAP_INSTANCE_KEY) =>
  (state: RootState) =>
    state.maps.mapContext[mapId]?.mapRulerToolPolylinesAndMarkers;
export const selectSelectedAntennaTypeId =
  (mapId = GOOGLE_MAP_INSTANCE_KEY) =>
  (state: RootState) =>
    state.maps.mapContext[mapId]?.overlaysUiState[state.maps.overlaysCurrentContext]?.selectedAntennaTypeId;
export const selectLoadedSscfVersion =
  (mapId = GOOGLE_MAP_INSTANCE_KEY) =>
  (state: RootState) =>
    state.maps.mapContext[mapId]?.overlaysUiState[state.maps.overlaysCurrentContext]?.selectedSscfVersion;
export const selectRlFlToggleState =
  (mapId = GOOGLE_MAP_INSTANCE_KEY) =>
  (state: RootState) =>
    state.maps.mapContext[mapId]?.overlaysUiState[state.maps.overlaysCurrentContext]?.rlFlToggleState;
export const selectHeatMapsEnabled =
  (mapId = GOOGLE_MAP_INSTANCE_KEY) =>
  (state: RootState) =>
    state.maps.mapContext[mapId]?.overlaysUiState[state.maps.overlaysCurrentContext]?.heatMapsEnabled;
export const selectGroundStationsEnabled =
  (mapId = GOOGLE_MAP_INSTANCE_KEY) =>
  (state: RootState) =>
    state.maps.mapContext[mapId]?.overlaysUiState[state.maps.overlaysCurrentContext]?.groundStationsEnabled;
export const selectAntennaDropDownDisabled =
  (mapId = GOOGLE_MAP_INSTANCE_KEY) =>
  (state: RootState) =>
    state.maps.mapContext[mapId]?.overlaysUiState[state.maps.overlaysCurrentContext]?.antennaDropDownDisabled;
export const selectOpenedExpansionPanelId =
  (mapId = GOOGLE_MAP_INSTANCE_KEY) =>
  (state: RootState) =>
    state.maps.mapContext[mapId]?.overlaysUiState[state.maps.overlaysCurrentContext]?.openedExpansionPanelId;
export const selectBeamsOnMap =
  (mapId = GOOGLE_MAP_INSTANCE_KEY) =>
  (state: RootState) =>
    state.maps.mapContext[mapId]?.overlaysUiState[state.maps.overlaysCurrentContext]?.beamsOnMap;
export const selectKuHnaZonesChecked =
  (mapId = GOOGLE_MAP_INSTANCE_KEY) =>
  (state: RootState) =>
    state.maps.mapContext[mapId]?.overlaysUiState[state.maps.overlaysCurrentContext]?.kuHnaZonesChecked;
export const selectKaHnaZonesChecked =
  (mapId = GOOGLE_MAP_INSTANCE_KEY) =>
  (state: RootState) =>
    state.maps.mapContext[mapId]?.overlaysUiState[state.maps.overlaysCurrentContext]?.kaHnaZonesChecked;
export const selectHcxHnaZonesChecked =
  (mapId = GOOGLE_MAP_INSTANCE_KEY) =>
  (state: RootState) =>
    state.maps.mapContext[mapId]?.overlaysUiState[state.maps.overlaysCurrentContext]?.hcxHnaZonesChecked;
export const selectGxHnaZonesChecked =
  (mapId = GOOGLE_MAP_INSTANCE_KEY) =>
  (state: RootState) =>
    state.maps.mapContext[mapId]?.overlaysUiState[state.maps.overlaysCurrentContext]?.gxHnaZonesChecked;
export const selectKuHnaZonesDisabled =
  (mapId = GOOGLE_MAP_INSTANCE_KEY) =>
  (state: RootState) =>
    state.maps.mapContext[mapId]?.overlaysUiState[state.maps.overlaysCurrentContext]?.kuHnaZonesDisabled;
export const selectKaHnaZonesDisabled =
  (mapId = GOOGLE_MAP_INSTANCE_KEY) =>
  (state: RootState) =>
    state.maps.mapContext[mapId]?.overlaysUiState[state.maps.overlaysCurrentContext]?.kaHnaZonesDisabled;
export const selectHcxHnaZonesDisabled =
  (mapId = GOOGLE_MAP_INSTANCE_KEY) =>
  (state: RootState) =>
    state.maps.mapContext[mapId]?.overlaysUiState[state.maps.overlaysCurrentContext]?.hcxHnaZonesDisabled;
export const selectGxHnaZonesDisabled =
  (mapId = GOOGLE_MAP_INSTANCE_KEY) =>
  (state: RootState) =>
    state.maps.mapContext[mapId]?.overlaysUiState[state.maps.overlaysCurrentContext]?.gxHnaZonesDisabled;
export const selectFlightPathBeamsDisabled =
  (mapId = GOOGLE_MAP_INSTANCE_KEY) =>
  (state: RootState) =>
    state.maps.mapContext[mapId]?.overlaysUiState[state.maps.overlaysCurrentContext]?.flightPathBeamsDisabled;
export const selectFlightPathWaypointsDisabled =
  (mapId = GOOGLE_MAP_INSTANCE_KEY) =>
  (state: RootState) =>
    state.maps.mapContext[mapId]?.overlaysUiState[state.maps.overlaysCurrentContext]?.flightPathWaypointsDisabled;
export const selectCurrentNextBeamsDisabled =
  (mapId = GOOGLE_MAP_INSTANCE_KEY) =>
  (state: RootState) =>
    state.maps.mapContext[mapId]?.overlaysUiState[state.maps.overlaysCurrentContext]?.currentNextBeamsDisabled;
export const selectCurrentNextBeamsChecked =
  (mapId = GOOGLE_MAP_INSTANCE_KEY) =>
  (state: RootState) =>
    state.maps.mapContext[mapId]?.overlaysUiState[state.maps.overlaysCurrentContext]?.currentNextBeamsChecked;
export const selectCurrentBeams =
  (mapId = GOOGLE_MAP_INSTANCE_KEY) =>
  (state: RootState) =>
    state.maps.mapContext[mapId]?.overlaysUiState[state.maps.overlaysCurrentContext]?.currentBeams;
export const selectNextBeam =
  (mapId = GOOGLE_MAP_INSTANCE_KEY) =>
  (state: RootState) =>
    state.maps.mapContext[mapId]?.overlaysUiState[state.maps.overlaysCurrentContext]?.nextBeam;

// Data Usage
export const selectTotalUsageByNetwork = (state: RootState) => state.dataUsage.totalUsageByNetwork;
export const selectRequestingTotalUsageByNetwork = (state: RootState) => state.dataUsage.requestingTotalUsageByNetwork;
export const selectReceivedTotalUsageByNetworkError = (state: RootState) =>
  state.dataUsage.receivedTotalUsageByNetworkError;

export const selectTotalUsageByLink = (state: RootState) => state.dataUsage.totalUsageByLink;
export const selectRequestingTotalUsageByLink = (state: RootState) => state.dataUsage.requestingTotalUsageByLink;
export const selectReceivedTotalUsageByLinkError = (state: RootState) => state.dataUsage.receivedTotalUsageByLinkError;

export const selectUsageByLink = (state: RootState) => state.dataUsage.usageByLink;
export const selectRequestingUsageByLink = (state: RootState) => state.dataUsage.requestingUsageByLink;
export const selectReceivedUsageByLinkError = (state: RootState) => state.dataUsage.receivedUsageByLinkError;

export const selectCumulativeUsageByLink = (state: RootState) => state.dataUsage.cumulativeUsageByLink;
export const selectRequestingCumulativeUsageByLink = (state: RootState) =>
  state.dataUsage.requestingCumulativeUsageByLink;
export const selectReceivedCumulativeUsageByLinkError = (state: RootState) =>
  state.dataUsage.receivedCumulativeUsageByLinkError;

export const selectUsageByNetwork = (state: RootState) => state.dataUsage.usageByNetwork;
export const selectRequestingUsageByNetwork = (state: RootState) => state.dataUsage.requestingUsageByNetwork;
export const selectReceivedUsageByNetworkError = (state: RootState) => state.dataUsage.receivedUsageByNetworkError;

export const selectCumulativeUsageByNetwork = (state: RootState) => state.dataUsage.cumulativeUsageByNetwork;
export const selectRequestingCumulativeUsageByNetwork = (state: RootState) =>
  state.dataUsage.requestingCumulativeUsageByNetwork;
export const selectReceivedCumulativeUsageByNetworkError = (state: RootState) =>
  state.dataUsage.receivedCumulativeUsageByNetworkError;

export const selectDataUsageLoading = (state: RootState) =>
  state.dataUsage.requestingTotalUsageByNetwork ||
  state.dataUsage.requestingUsageByNetwork ||
  state.dataUsage.requestingUsageByLink ||
  state.dataUsage.requestingCumulativeUsageByNetwork ||
  state.dataUsage.requestingCumulativeUsageByLink ||
  state.dataUsage.requestingServiceAvailability;

export const selectTotalPacketLossByLink = (state: RootState) => state.dataUsage.totalPacketLossByLink;

export const selectServiceAvailability = (state: RootState) => state.dataUsage.serviceAvailability;

//HNA override
export const selectHnaOverrideData = (state: RootState) => state.missionPlans.hnaOverrideData;
export const selectIsRequestingHnaOverrideData = (state: RootState) => state.missionPlans.isRequestingHnaOverrideData;
export const selectRequestingHnaOverrideDataError = (state: RootState) =>
  state.missionPlans.requestingHnaOverrideDataError;

// Service Tier Requests
export const selectServiceTierRequests = (state: RootState) => state.serviceTierRequests.serviceTierRequests;
export const selectIsRequestingServiceTierRequests = (state: RootState) =>
  state.serviceTierRequests.isRequestingServiceTierRequests;
export const selectRequestingServiceTierRequestsError = (state: RootState) =>
  state.serviceTierRequests.requestingServiceTierRequestsError;
//map services to an array and fitler out completed/denied services
export const selectPendingServiceRequestsAsArray = (state: RootState) => {
  const array = Object.keys(state.serviceTierRequests.serviceTierRequests).map(
    (serviceId: string) => state.serviceTierRequests.serviceTierRequests[serviceId],
  );

  return array.filter(
    service =>
      service.status !== ServiceTierRequestStatus.COMPLETED && service.status !== ServiceTierRequestStatus.DENIED,
  );
};

export const selectCurrentServiceTierChangeRequest = (state: RootState) =>
  state.serviceTierRequests.currentServiceTierChangeRequest;
export const selectIsRequestingCurrentServiceTierChangeRequest = (state: RootState) =>
  state.serviceTierRequests.isRequestingCurrentServiceTierChangeRequest;
export const selectRequestingCurrentServiceTierChangeRequestError = (state: RootState) =>
  state.serviceTierRequests.requestingCurrentServiceTierChangeRequestError;

export const selectIsUpdatingServiceTierRequest = (state: RootState) =>
  state.serviceTierRequests.isUpdatingServiceTierRequest;
export const selectUpdatingServiceTierRequestError = (state: RootState) =>
  state.serviceTierRequests.updatingServiceTierRequestError;
export const selectIsRequestingPostServiceTierRequest = (state: RootState) =>
  state.serviceTierRequests.isRequestingPostServiceTierRequest;
export const selectRequestingPostServiceTierRequestSuccess = (state: RootState) =>
  state.serviceTierRequests.requestingPostServiceTierRequestSuccess;
export const selectRequestingPostServiceTierRequestError = (state: RootState) =>
  state.serviceTierRequests.requestingPostServiceTierRequestError;

export const selectAvailableServiceTiers = (state: RootState) => state.serviceTierRequests.availableServiceTiers;
export const selectIsRequestingAvailableServiceTiers = (state: RootState) =>
  state.serviceTierRequests.isRequestingAvailableServiceTiers;
export const selectRequestingAvailableServiceTiersError = (state: RootState) =>
  state.serviceTierRequests.requestingAvailableServiceTiersError;

export const selectIsDeletingServiceTierChangeRequest = (state: RootState) =>
  state.serviceTierRequests.isDeletingServiceTierChangeRequest;
export const selectRequestingDeleteServiceTierChangeRequestError = (state: RootState) =>
  state.serviceTierRequests.requestingDeleteServiceTierChangeRequestError;
export const selectRequestingDeleteServiceTierChangeRequestSuccess = (state: RootState) =>
  state.serviceTierRequests.requestingDeleteServiceTierChangeRequestSuccess;

// Manual Downtime Periods
export const selectManualDowntimePeriods = (state: RootState) => state.manualDowntimePeriods.manualDowntimePeriods;

export const selectIsRequestingManualDowntimePeriods = (state: RootState) =>
  state.manualDowntimePeriods.isRequestingManualDowntimePeriods;
export const selectRequestManualDowntimePeriodsError = (state: RootState) =>
  state.manualDowntimePeriods.requestManualDowntimePeriodsError;

export const selectIsAddingManualDowntimePeriod = (state: RootState) =>
  state.manualDowntimePeriods.isAddingManualDowntimePeriod;
export const selectAddedManualDowntimePeriodError = (state: RootState) =>
  state.manualDowntimePeriods.addedManualDowntimePeriodError;

export const selectIsDeletingManualDowntimePeriod = (state: RootState) =>
  state.manualDowntimePeriods.isDeletingManualDowntimePeriod;
export const selectDeletedManualDowntimePeriodError = (state: RootState) =>
  state.manualDowntimePeriods.deletedManualDowntimePeriodError;
export const selectHaloNetSpaceVehicles = (state: RootState) => state.haloNetSpaceVehicles.spaceVehicles;
export const selectHaloNetSpaceVehicleOrbitsBySpaceVehicleId = (state: RootState) =>
  state.haloNetSpaceVehicles.orbitsBySpaceVehicleId;
export const selectIsRequestingHaloNetSpaceVehicles = (state: RootState) =>
  state.haloNetSpaceVehicles.isRequestingSpaceVehicles;
export const selectIsRequestingHaloNetSpaceVehicleOrbits = (state: RootState) =>
  state.haloNetSpaceVehicles.isRequestingOrbits;
export const selectHaloNetSpaceVehiclesError = (state: RootState) => state.haloNetSpaceVehicles.spaceVehiclesError;
export const selectHaloNetSpaceVehicleOrbitsError = (state: RootState) => state.haloNetSpaceVehicles.orbitsError;

// The orbits to draw, keyed by svName. Space vehicles hidden from the map are left out.
export const selectVisibleSpaceVehicleOrbitsBySvName = (state: RootState): OrbitPointsBySvName => {
  const hiddenDatapathUuids = selectHiddenTerminalsList(state) || [];
  const spaceVehicles = selectHaloNetLinkedSpaceVehicleTerminalsAsArray(state);

  // Restricted or APR blanked terminals should not show location information
  const withheldSvNames = new Set(
    spaceVehicles
      .filter((terminal: Terminal) => isPositionWithheld(terminal))
      .map((terminal: Terminal) => terminal.haloNetSpaceVehicleName as string),
  );

  const visibleSvNames = new Set(
    spaceVehicles
      .filter((terminal: Terminal) => !hiddenDatapathUuids.includes(terminal.datapathUuid))
      .map((terminal: Terminal) => terminal.haloNetSpaceVehicleName as string),
  );

  return Object.entries(state.haloNetSpaceVehicles.orbitsBySpaceVehicleId).reduce(
    (accumulator: OrbitPointsBySvName, [svName, orbitPoints]: [string, OrbitPoint[]]) => {
      if (visibleSvNames.has(svName) && !withheldSvNames.has(svName)) {
        accumulator[svName] = orbitPoints;
      }
      return accumulator;
    },
    {},
  );
};

// The svName of the space vehicle in the details panel, if one is open.
export const selectSelectedSpaceVehicleSvName = (state: RootState): string | undefined =>
  state.terminals.tailDetailsPanelTerminal?.haloNetSpaceVehicleName;

// The orbit of the space vehicle in the details panel, if HaloNet has one.
const selectSelectedSpaceVehicleOrbit = (state: RootState): OrbitPoint[] | undefined => {
  const svName = selectSelectedSpaceVehicleSvName(state);

  return svName ? state.haloNetSpaceVehicles.orbitsBySpaceVehicleId[svName] : undefined;
};

export const selectHasSelectedSpaceVehicleOrbit = (state: RootState): boolean =>
  !!selectSelectedSpaceVehicleOrbit(state)?.length;

// A terminal history of the space vehicle in the details panel, with the altitude and the speed
// that the details panel shows.
const toSelectedSpaceVehicleChartHistory = (state: RootState, history: { [key: number]: TerminalHistory }) => {
  const terminal = state.terminals.tailDetailsPanelTerminal;

  return toSpaceVehicleChartHistory(
    history,
    selectSelectedSpaceVehicleOrbit(state),
    !terminal || isPositionWithheld(terminal),
    selectHaloNetPositionFallbackAfterSeconds(state),
  );
};

export const selectSpaceVehicleChartTerminalHistory = (state: RootState) =>
  toSelectedSpaceVehicleChartHistory(state, state.terminalHistory.normalizedTerminalHistory);
export const selectSpaceVehicleChartOtherTerminalHistory = (state: RootState) =>
  toSelectedSpaceVehicleChartHistory(state, state.terminalHistory.normalizedOtherTerminalHistory);

// Organizations
export const selectOrganizations = (state: RootState) => state.organizations.organizations;
export const selectCurrentOrganization = (state: RootState) => state.organizations.currentOrganization;

export const selectIsRequestingOrganizations = (state: RootState) => state.organizations.isRequestingOrganizations;
export const selectOrganizationsRequestError = (state: RootState) => state.organizations.organizationsRequestError;

export const selectIsRequestingCurrentOrganization = (state: RootState) =>
  state.organizations.isRequestingCurrentOrganization;
export const selectCurrentOrganizationRequestError = (state: RootState) =>
  state.organizations.currentOrganizationRequestError;

export const selectIsProvisioningOrganization = (state: RootState) => state.organizations.isProvisioningOrganization;
export const selectOrganizationProvisioningError = (state: RootState) =>
  state.organizations.organizationProvisioningError;

export const selectIsDeletingOrganization = (state: RootState) => state.organizations.isDeletingOrganization;
export const selectDeletingOrganizationError = (state: RootState) => state.organizations.deleteOrganizationError;

export const selectIsUpdatingOrganization = (state: RootState) => state.organizations.isUpdatingOrganization;
export const selectUpdatingOrganizationError = (state: RootState) => state.organizations.updateOrganizationError;

export const selectIsRequestingOrganizationTerminals = (state: RootState) =>
  state.organizations.isRequestingOrganizationTerminals;
export const selectOrganizationTerminalsRequestError = (state: RootState) =>
  state.organizations.organizationTerminalsRequestError;

// Organizations
export const selectContracts = (state: RootState) => state.contracts.contracts;
export const selectCurrentContract = (state: RootState) => state.contracts.currentContract;

export const selectIsRequestingContracts = (state: RootState) => state.contracts.isRequestingContracts;
export const selectContractsRequestError = (state: RootState) => state.contracts.contractsRequestError;

export const selectIsRequestingCurrentContract = (state: RootState) => state.contracts.isRequestingCurrentContract;
export const selectCurrentContractRequestError = (state: RootState) => state.contracts.currentContractRequestError;

export const selectIsProvisioningContract = (state: RootState) => state.contracts.isProvisioningContract;
export const selectContractProvisioningError = (state: RootState) => state.contracts.contractProvisioningError;

export const selectIsDeletingContract = (state: RootState) => state.contracts.isDeletingContract;
export const selectDeletingContractError = (state: RootState) => state.contracts.deleteContractError;

export const selectIsUpdatingContract = (state: RootState) => state.contracts.isUpdatingContract;
export const selectUpdatingContractError = (state: RootState) => state.contracts.updateContractError;

// Roles
export const selectRoles = (state: RootState) => state.roles.roles;
export const selectCurrentRole = (state: RootState) => state.roles.currentRole;

export const selectIsRequestingRoles = (state: RootState) => state.roles.isRequestingRoles;
export const selectRolesRequestError = (state: RootState) => state.roles.rolesRequestError;

export const selectIsRequestingCurrentRole = (state: RootState) => state.roles.isRequestingCurrentRole;
export const selectCurrentRoleRequestError = (state: RootState) => state.roles.currentRoleRequestError;

export const selectIsProvisioningRole = (state: RootState) => state.roles.isProvisioningRole;
export const selectRoleProvisioningError = (state: RootState) => state.roles.roleProvisioningError;

export const selectIsDeletingRole = (state: RootState) => state.roles.isDeletingRole;
export const selectDeletingRoleError = (state: RootState) => state.roles.deleteRoleError;

export const selectIsUpdatingRole = (state: RootState) => state.roles.isUpdatingRole;
export const selectUpdatingRoleError = (state: RootState) => state.roles.updateRoleError;

// Clients
export const selectClients = (state: RootState) => state.clients.clients;
export const selectCurrentClient = (state: RootState) => state.clients.currentClient;

export const selectIsRequestingClients = (state: RootState) => state.clients.isRequestingClients;
export const selectClientsRequestError = (state: RootState) => state.clients.clientsRequestError;

export const selectIsRequestingCurrentClient = (state: RootState) => state.clients.isRequestingCurrentClient;
export const selectCurrentClientRequestError = (state: RootState) => state.clients.currentClientRequestError;

export const selectIsProvisioningClient = (state: RootState) => state.clients.isProvisioningClient;
export const selectClientProvisioningError = (state: RootState) => state.clients.clientProvisioningError;

export const selectIsDeletingClient = (state: RootState) => state.clients.isDeletingClient;
export const selectDeletingClientError = (state: RootState) => state.clients.deleteClientError;

export const selectIsUpdatingClient = (state: RootState) => state.clients.isUpdatingClient;
export const selectUpdatingClientError = (state: RootState) => state.clients.updateClientError;

// Options
export const selectNetworkOptions = (state: RootState) => state.options.networkOptions;
export const selectIsRequestingNetworkOptions = (state: RootState) => state.options.isRequestingNetworkOptions;
export const selectNetworkOptionsRequestError = (state: RootState) => state.options.networkOptionsRequestError;

export const selectTerminalTypeOptions = (state: RootState) => state.options.terminalTypeOptions;
export const selectIsRequestingTerminalTypeOptions = (state: RootState) =>
  state.options.isRequestingTerminalTypeOptions;
export const selectTerminalTypeOptionsRequestError = (state: RootState) =>
  state.options.terminalTypeOptionsRequestError;

export const selectPlatformTypeOptions = (state: RootState) => state.options.platformTypeOptions;
export const selectIsRequestingPlatformTypeOptions = (state: RootState) =>
  state.options.isRequestingPlatformTypeOptions;
export const selectPlatformTypeOptionsRequestError = (state: RootState) =>
  state.options.platformTypeOptionsRequestError;

export const selectDatapathLabelOptions = (state: RootState) => state.options.datapathLabelOptions;
export const selectIsRequestingDatapathLabelOptions = (state: RootState) =>
  state.options.isRequestingDatapathLabelOptions;
export const selectDatapathLabelOptionsRequestError = (state: RootState) =>
  state.options.datapathLabelOptionsRequestError;

export const selectCustomerGroupTypeOptions = (state: RootState) => state.options.customerGroupOptions;
export const selectIsRequestingCustomerGroupTypeOptions = (state: RootState) =>
  state.options.isRequestingCustomerGroupOptions;
export const selectCustomerGroupTypeOptionsRequestError = (state: RootState) =>
  state.options.customerGroupOptionsRequestError;

export const selectAntennaTypeOptions = (state: RootState) => state.options.antennaOptions;
export const selectIsRequestingAntennaTypeOptions = (state: RootState) => state.options.isRequestingAntennaOptions;
export const selectAntennaTypeOptionsRequestError = (state: RootState) => state.options.antennaOptionsRequestError;

export const selectModemTypeOptions = (state: RootState) => state.options.modemTypeOptions;
export const selectIsRequestingModemTypeOptions = (state: RootState) => state.options.isRequestingModemTypeOptions;
export const selectModemTypeOptionsRequestError = (state: RootState) => state.options.modemTypeOptionsRequestError;

// GPS SA selectors
export const selectGpsSaLowAccuracyMapLayers = (state: RootState) => state.gpsSa.gpsSaLowAccuracyMapLayers;
export const selectGpsSaReducedAccuracyMapLayers = (state: RootState) => state.gpsSa.gpsSaReducedAccuracyMapLayers;
export const selectGpsSaNormalAccuracyMapLayers = (state: RootState) => state.gpsSa.gpsSaNormalAccuracyMapLayers;
export const selectIsRequestingGpsSaMapLayers = (state: RootState) => state.gpsSa.isRequestingGpsSaMapLayers;
export const selectGpsSaMapLayersRequestFinished = (state: RootState) => state.gpsSa.isGpsSaMapLayersRequestFinished;
export const selectGpsSaMapLayersRequestError = (state: RootState) => state.gpsSa.gpsSaMapLayersRequestError;

export const selectGpsSaMapLayersChecked =
  (mapId = GOOGLE_MAP_INSTANCE_KEY) =>
  (state: RootState) =>
    state.maps.mapContext[mapId]?.overlaysUiState[state.maps.overlaysCurrentContext]?.gpsSaMapLayersChecked;

export const selectGpsSaMapLayersLowAccuracyChecked =
  (mapId = GOOGLE_MAP_INSTANCE_KEY) =>
  (state: RootState) =>
    state.maps.mapContext[mapId]?.overlaysUiState[state.maps.overlaysCurrentContext]?.gpsSaMapLayersLowAccuracyChecked;

export const selectGpsSaMapLayersReducedAccuracyChecked =
  (mapId = GOOGLE_MAP_INSTANCE_KEY) =>
  (state: RootState) =>
    state.maps.mapContext[mapId]?.overlaysUiState[state.maps.overlaysCurrentContext]
      ?.gpsSaMapLayersReducedAccuracyChecked;

export const selectGpsSaMapLayersNormalAccuracyChecked =
  (mapId = GOOGLE_MAP_INSTANCE_KEY) =>
  (state: RootState) =>
    state.maps.mapContext[mapId]?.overlaysUiState[state.maps.overlaysCurrentContext]
      ?.gpsSaMapLayersNormalAccuracyChecked;
